import React from "react";
import {list} from "../../../../../utils/array";
import AbortablePromise from "../../../../../utils/promise/AbortablePromise";
import PropsProxy from "../../../../layout/PropsProxy";
import {ProviderProps} from "../Types/ProviderProps";
import {RequestParams} from "../Types/RequestParams";
import {FilterType} from "../Types/FilterType";
import {OrderType} from "../Types/OrderType";
import {AggregationsFieldType} from "../Types/Aggregations";
import {defaultPageSize} from "../defaults";
import {Items} from "../Types/Items";

export const rowKeyField = "_____id " + (new Date().getTime());

const handler = {
    get(target: any, p: string | symbol, receiver: any): any {
        if (p == "forEach" && target && typeof target === "object" && !Object.keys(target).find((k) => isNaN(k))) {
            return <T,>(cb: (item: T, k: string|number|symbol, target: Record<string | number | symbol, any>) => void) => {
                return Object.keys(target).forEach((k) => {
                    cb(target[k], k, target);
                });
            }
        }
        if (p == "map" && target && typeof target === "object" && !Object.keys(target).find((k) => isNaN(k))) {
            return <T,>(cb: (item: T, k: string|number|symbol, target: Record<string | number | symbol, any>) => void) => {
                return Object.keys(target).map((k) => {
                    return cb(target[k], k, target);
                });
            }
        }
        if (p == "filter" && target && typeof target === "object" && !Object.keys(target).find((k) => isNaN(k))) {
            return <T,>(cb: (item: T, k: string|number|symbol, target: Record<string | number | symbol, any>) => void) => {
                return Object.keys(target).filter((k) => {
                    return cb(target[k], k, target);
                });
            }
        }
        if (p == "reduce" && target && typeof target === "object" && !Object.keys(target).find((k) => isNaN(k))) {
            return <T,I>(cb: (prev: T, curr: I, idx: string|number|symbol, items: I[]) => T, c: T) => {
                return Object.keys(target).reduce((previousValue, currentValue, currentIndex, array) => {
                    return cb(previousValue, target[currentValue], currentIndex, target);
                }, c);
            }
        }
        return Reflect.get(target, p, receiver);
    }
};

export default abstract class DataProviderComp<Props extends ProviderProps, State> extends React.Component<Props, State> {

    public props: Props;

    public state: State;

    public rowKeyField: symbol|string|number;

    constructor(props: Props) {
        super(props);
        this.addItems = this.addItems.bind(this);
        this.editItems = this.editItems.bind(this);
        this.removeItems = this.removeItems.bind(this);
        this.generateRequest = this.generateRequest.bind(this);
        this.generateRequestFilter = this.generateRequestFilter.bind(this);
        this.generateRequestOrder = this.generateRequestOrder.bind(this);
        this.generateRequestAggregationFields = this.generateRequestAggregationFields.bind(this);
        this.parseView = this.parseView.bind(this);
        this.rowKeyField = rowKeyField;
    }

    async addItems(items: Items): Promise<any> {
        return new Proxy(items, handler);
    }

    async editItems(items: Items): Promise<any> {
        return new Proxy(items, handler);
    }

    async removeItems(items: Items): Promise<any> {
        return new Proxy(items, handler);
    }

    generateRequest(props: Props, state: State): RequestParams {
        let filter = this.generateRequestFilter(props, state);
        if (filter) {
            filter = JSON.parse(JSON.stringify(filter));
        }
        let order = this.generateRequestOrder(props, state);
        if (order) {
            order = JSON.parse(JSON.stringify(order));
        }
        let aggregationFields = this.generateRequestAggregationFields(props, state);
        return {
            itemsperpage: props.pageSize ?? defaultPageSize,
            page: props.page || 1,
            getcolumns: true,
            withheader: true,
            filter,
            view: this.parseView(props),
            option: props.option || "componentlist",
            aggregations: aggregationFields ? {
                fields: aggregationFields
            } : undefined,
            group: props.groupBy,
            order,
            parameters: props.parameters,
            ...props.reqparams,
        };
    }

    generateRequestFilter(props: Props, state: State): FilterType|FilterType[]|null|undefined {
        let filter: FilterType = undefined;
        if (props.filter) {
            filter = list(props.filter);
        }
        Object.entries(state.columnState).map((entry) => {
            if (state.visibleFields.indexOf(entry[0]) !== -1) {
                if (!filter && entry[1].filter) {
                    filter = list(entry[1].filter);
                } else if (entry[1].filter) {
                    filter = list(filter, entry[1].filter);
                }
            }
        });
        return filter;
    }

    generateRequestOrder(props: Props, state: State): OrderType|OrderType[]|null|undefined {
        let order = undefined;
        if (props.order) {
            order = list(props.order);
        }
        Object.entries(state.columnState).map((entry) => {
            if (state.visibleFields.indexOf(entry[0]) !== -1) {
                if (!order && entry[1].order) {
                    order = list(entry[1].order);
                } else if (entry[1].order) {
                    if (Array.isArray(entry[1].order)) {
                        order = list(order, ...entry[1].order);
                    } else {
                        order = list(order, entry[1].order);
                    }
                }
            }
        });
        if (!order?.length) {
            if (props.fallbackOrder) {
                order = list(props.fallbackOrder);
            }
        }
        return order;
    }

    generateRequestAggregationFields(props: Props, state: State): AggregationsFieldType {
        let aggregationFields: AggregationsFieldType = undefined;
        Object.entries(props.aggregations?.fields ?? []).map((entry) => {
            if (!aggregationFields) {
                aggregationFields = {};
            }
            aggregationFields[entry[0]] = entry[1];
        });
        props.getFields().map((field) => {
            if (field.props.aggregation) {
                if (!aggregationFields) {
                    aggregationFields = {};
                }
                aggregationFields[field.props.field] = field.props.aggregation;
            }
        });
        return aggregationFields;
    }

    parseView(props: Props): object {
        return {};
    }

    abstract loadData(request: RequestParams): AbortablePromise<ResponseData>

    render() {
        return (
            <PropsProxy
                {...this.props}
                rowKeyField={this.rowKeyField}
                loadData={this.loadData}
                addItems={this.addItems}
                editItems={this.editItems}
                removeItems={this.removeItems}
                generateRequest={this.generateRequest}
                parseView={this.parseView}
            >
                {this.props.children}
            </PropsProxy>
        );
    }
}